import { IStateService } from 'angular-ui-router';
import { each, filter, map } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ISubscription } from 'rxjs/Subscription';
import { SdcActionsService } from 'src/app/sdc/flux/sdc-actions.service';
import { ISdcStore } from 'src/app/sdc/flux/sdc-store.interface';
import { StoreListenerUtils } from 'src/app/shared/services/store-listener-utils/store-listener-utils.service';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { IStudentSupport } from 'Src/ng2/shared/typings/interfaces/student-support.interface';
import { SupportStatuses } from '../../../../ng2/shared/constants/support-statuses.constant';
import { ISchoolSupport, ISchoolSupportExtended } from '../../../../ng2/shared/typings/interfaces/support.interface';
import { ImSupport } from '../../../shared/static-models/im-models/im-support.service';
import { SpinnerModal } from './../../../shared/modals/status-spinner/status-spinner.service';

type TSupportsTableColumns = 'sy' | 'category' | 'name' | 'activity_lead' | 'dates' | 'meets_on' | 'location';

export class SupportsTableCtrl {
  static $inject = [
    '$scope',
    '$state',
    '$mdDialog',
    'ConfirmationModal',
    'Support',
    'SpinnerModal',
    'Rollbar',
    'SDCStore',
    'sdcActions',
    'StoreListenerUtils',
    'ImSupport',
    'UtilitiesService',
  ];

  // bindings
  public schoolSupports: ISchoolSupport[];
  onSupportUpdate;
  currentUser;

  studentSupportsSubscription: ISubscription;
  studentSupports$: Observable<IStudentSupport[]>;
  studentSupports: IStudentSupport[];

  // other
  public browserTab: string;
  public currentSupportsGrouped: any;
  public supportsGroupedByStatus: { [key: string]: ISchoolSupport[] };
  private enrollments;

  public supportsGroupedByStatusSortConfig = {
    [SupportStatuses.frontend.IN_PROGRESS]: {
      activeSort: 'dates',
      reverseSort: false,
    },
    [SupportStatuses.frontend.NOT_STARTED]: {
      activeSort: 'dates',
      reverseSort: false,
    },
    [SupportStatuses.frontend.COMPLETED]: {
      activeSort: 'dates',
      reverseSort: false,
    },
  };

  readonly SUPPORTS_STATUS_HEADLINE = {
    [SupportStatuses.frontend.NOT_STARTED]: 'Not Started',
    [SupportStatuses.frontend.IN_PROGRESS]: 'In Progress',
    [SupportStatuses.frontend.COMPLETED]: 'Completed',
  };

  constructor(
    public $scope,
    private $state: IStateService,
    private $mdDialog: any,
    private ConfirmationModal: any,
    private Support: any,
    private SpinnerModal: SpinnerModal,
    private Rollbar,
    private SDCStore: ISdcStore,
    private sdcActions: SdcActionsService,
    private storeListenerUtils: StoreListenerUtils,
    private imSupport: ImSupport,
    private utilitiesService: UtilitiesService,
  ) {}

  public destroy$: Subject<boolean> = new Subject<boolean>();

  $onInit(): void {
    this.storeListenerUtils.startListeningAndLoad(this, [['schoolSupports']]);
  }

  public setUi(): void {
    this.studentSupportsSubscription = this.studentSupports$
      .pipe(
        tap((studentSupports: IStudentSupport[]) => {
          this.studentSupports = studentSupports;
          this.setView();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  setView(): void {
    const storedSchoolSupports = angular.copy(this.SDCStore.getAllSchoolSupports());
    this.schoolSupports = this.getActiveSupportsWithStatusObj(storedSchoolSupports);
    this.supportsGroupedByStatus = this.imSupport.getSupportsGroupedByFrontEndStatus(this.schoolSupports);
    this.sortSupportsGroupedByStatus();
    this.browserTab = 'current';
    this.SpinnerModal.deactivateSpinner();
  }

  getActiveSupportsWithStatusObj(supports: ISchoolSupport[]): ISchoolSupportExtended[] {
    const activeSupports = this.Support.getNotDeletedSupports(supports);

    const activeSupportsForDisplay = map(activeSupports, (support: ISchoolSupportExtended) => {
      support._formattedSchoolYear = this.imSupport.getFormattedSchoolYear(support);
      support._startAndEndDateString = this.imSupport.getStartAndEndDateString(support);
      support._startAndEndTimeString = this.imSupport.getStartAndEndTimeString(support);
      support._leadHumanName = this.imSupport.getLeadHumanName(support);
      support._meetsOnScheduleString = this.imSupport.getMeetsOnScheduleString(support);
      support._categoryHumanName = this.imSupport.getCategoryHumanName(support);

      return support;
    });

    return activeSupportsForDisplay;
  }

  // TABLE MENU
  openMenu($mdOpenMenu, ev) {
    $mdOpenMenu(ev);
  }

  // ACTIONS
  editSupport(supportId): void {
    const state = 'main.school.settings.supports.edit';
    const param = { supportId };
    this.$state.go(state, param);
  }

  deleteSupport(support): void {
    this.enrollments = filter(this.studentSupports, (studentSupport: IStudentSupport) => {
      return support._id === studentSupport.support.supportId;
    });

    let snippet;
    if (this.enrollments.length === 0) {
      snippet = `No students are currently assigned to this support.`;
    } else {
      snippet = `This will remove ${this.enrollments.length} students from the support.`;
    }
    const text = `You are about to delete ${support.name}. ${snippet} Are you sure you want to do this?`;
    const title = 'Confirm Deletion';
    const affirmationText = 'Delete';
    const promise = this.ConfirmationModal.show(title, text, affirmationText);

    promise
      .then(() => {
        support.status = 'DELETED';
        const deletableSupport = new this.Support(support);
        deletableSupport.$patchFields(
          ['status'],
          () => {
            // TODO refreshSupports called `this.onSupportUpdate` on the parent. Is this neceessary? - JYR
            // this.refreshSupports();
            this.onSupportUpdate();
            const sdcPayload = {
              schoolSupportId: deletableSupport._id,
              patch: { status: 'DELETED' },
            };
            this.sdcActions.updateSchoolSupport(sdcPayload);
          },
          err => {
            console.error(err);
            this.Rollbar.error(`Unable to refresh supports`, err);
          },
        );
      })
      .catch(function(err) {
        this.$mdDialog.hide();
        this.Rollbar.error(`Unable to patch support's status field`, err);
      });
  }

  // TODO: does not seem to currently be used. Also, should it take a supportPojo as well? - JYR
  canCreate() {
    const support = new this.Support();
    return support.canCreate(this.currentUser);
  }

  canBeDeleted(supportPojo) {
    const support = new this.Support(supportPojo);
    return support.canDelete(this.currentUser);
  }

  canBeEdited(supportPojo): boolean {
    const support = new this.Support(supportPojo);
    // Disable edit button for Completed Support
    const canEdit =
      this.imSupport.getFrontendStatus(supportPojo) === 'COMPLETED' ? false : support.canEdit(this.currentUser);
    return canEdit;
  }

  sortSupportsGroupedByStatus() {
    each(this.supportsGroupedByStatus, (v: string[], k: string) => {
      this.sortColumn({
        columnName: this.supportsGroupedByStatusSortConfig[k].activeSort,
        value: v,
        groupStatusKey: k,
        reverseSort: this.supportsGroupedByStatusSortConfig[k].reverseSort,
      });
    });
  }

  sortColumn({
    columnName,
    value,
    groupStatusKey,
    reverseSort = !this.supportsGroupedByStatusSortConfig[groupStatusKey].reverseSort,
  }: {
    columnName: string;
    value: any;
    groupStatusKey: string;
    reverseSort: boolean;
  }) {
    this.supportsGroupedByStatusSortConfig[groupStatusKey].activeSort = columnName;
    this.supportsGroupedByStatusSortConfig[groupStatusKey].reverseSort = reverseSort;

    switch (columnName) {
      case 'sy':
        value.sort((a, b) =>
          this.utilitiesService.sortStrings({ a: a._formattedSchoolYear, b: b._formattedSchoolYear, reverseSort }),
        );
        break;
      case 'category':
        value.sort((a, b) =>
          this.utilitiesService.sortStrings({ a: a._categoryHumanName, b: b._categoryHumanName, reverseSort }),
        );
        break;
      case 'name':
        value.sort((a, b) => this.utilitiesService.sortStrings({ a: a.name, b: b.name, reverseSort }));
        break;
      case 'activity_lead':
        value.sort((a, b) =>
          this.utilitiesService.sortStrings({ a: a._leadHumanName, b: b._leadHumanName, reverseSort }),
        );
        break;
      case 'dates':
        value.sort((a, b) => {
          const dateFormat = 'MM:DD:YYYY';
          const aStartDate = a._startAndEndDateString.split('-')[0];
          const bStartDate = b._startAndEndDateString.split('-')[0];

          return this.utilitiesService.sortDates({ a: aStartDate, b: bStartDate, reverseSort, dateFormat });
        });
        break;
      case 'meets_on':
        value.sort((a, b) => {
          const dateFormat = 'h:mma';
          const aStartDate = a._startAndEndTimeString.split('-')[0];
          const bStartDate = b._startAndEndTimeString.split('-')[0];

          return this.utilitiesService.sortDates({ a: aStartDate, b: bStartDate, reverseSort, dateFormat });
        });
        break;
      case 'location':
        value.sort((a, b) => this.utilitiesService.sortStrings({ a: a.location, b: b.location, reverseSort }));
        break;
      default:
        break;
    }
  }
}
